<template>
  <div v-if="institutionalData" class="institutional-identification">

    <!-- LINHA 1: CADASTRO + STATUS -->
    <div class="row fit no-wrap items-center content-start q-pb-md">
      <div class="col-6 col-md-6">
        <div class="row">
          <span class="midia-app-caption-sm">Cadastro</span>
        </div>
        <div class="row">
          <span class="midia-app-content-lg">{{ institutionalData.nome }}</span>
        </div>
      </div>

      <div class="col-6 col-md-6">
        <div class="row">
          <span class="midia-app-label-lg q-pr-sm">Status:</span>
          <span class="midia-app-content-lg status-value">
            {{ getStatusLabel(institutionalData.status) }}
          </span>
        </div>
      </div>
    </div>

    <!-- LINHA 2: PERFIL + DATAS -->
    <div class="row fit no-wrap content-start q-pb-md">
      <div class="col-6 col-md-6">
        <div class="q-pb-md">
          <div class="row">
            <span class="midia-app-caption-sm">Perfil</span>
          </div>
          <div class="row">
            <span class="midia-app-content-lg">{{ institutionalData.perfil }}</span>
          </div>
        </div>

        <div class="row">
          <div class="col-4 col-md-4">
            <div class="row">
              <span class="midia-app-caption-sm">Data Inicial</span>
            </div>
            <div class="row">
              <span class="midia-app-content-lg">{{ institutionalData.dtIniVigencia }}</span>
            </div>
          </div>

          <div class="col-4 col-md-4">
            <div class="row">
              <span class="midia-app-caption-sm">Data Final</span>
            </div>
            <div class="row">
              <span class="midia-app-content-lg">{{ institutionalData.dtFimVigencia || '-' }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- CSV REDES / SUBREDES -->
      <div class="col-6 col-md-6">
        <div>
          <div class="row q-pb-xs">
            <span class="midia-app-label-sm">Redes / Sub-redes</span>
          </div>
          <div class="row fit no-wrap items-center q-pb-xs">
            <q-icon size="32px">
              <img src="@/assets/icons/icon_HiOutlineDocumentDownload.svg" alt="download" />
            </q-icon>
            <span class="midia-app-label-lg q-pl-sm">Rede / Subrede</span>
          </div>
          <div class="row q-py-xs">
            <c-q-btn
              propBtnLabel="Download"
              :propBtnFunction="onDownloadRedesCsv"
              :propBtnTransparent="true"
              :propBtnSize="EnumButtonSizes.SMALL"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- CSV PCS -->
    <!--<div class="row fit content-start q-pb-md"> -->
      <div v-if="showDownloadButtons !== false" class="row fit content-start q-pb-md">
      <div class="col-6 col-md-6">
        <div>
          <div class="row q-pb-xs">
            <span class="midia-app-label-sm">PCs</span>
          </div>

          <div class="row fit no-wrap items-center q-pb-xs">
            <q-icon size="32px">
              <img src="@/assets/icons/icon_HiOutlineDocumentDownload.svg" alt="download" />
            </q-icon>
            <span class="midia-app-label-lg q-pl-sm">PCs</span>
          </div>

          <div class="row q-py-xs">
            <!-- ✅ Se tem grupos, um botão por grupo -->
            <template v-if="temGrupos">
              <c-q-btn
                v-for="grupo in gruposDisponiveis"
                :key="grupo"
                :propBtnLabel="`Download ${grupo}`"
                :propBtnFunction="() => onDownloadPcsCsvPorGrupo(grupo)"
                :propBtnTransparent="true"
                :propBtnSize="EnumButtonSizes.SMALL"
                class="q-mr-sm"
              />
            </template>

            <!-- ✅ Se não tem grupos, botão único -->
            <c-q-btn
              v-else
              propBtnLabel="Download"
              :propBtnFunction="() => onDownloadPcsCsv()"
              :propBtnTransparent="true"
              :propBtnSize="EnumButtonSizes.SMALL"
            />
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { EnumButtonSizes } from '@/enums/enums'
import { CONST_INSTITUTIONAL_STATUS } from '@/static/constantsInstitutional'
import { useInstitutionalStore } from '@/stores/institutionalStore'
import { useRedeSubredeStore } from '@/stores/redeSubredeStore'
import messages from '@/utils/messages'

import { usePCStore } from '@/stores/pcStore'

const pcStore = usePCStore()

const redeSubredeStore = useRedeSubredeStore()
const institutionalStore = useInstitutionalStore()

const props = defineProps<{
  institutionalData: any
  showDownloadButtons?: boolean
}>()

// ✅ pcsPorGrupo do backend: { D0: [...], D1: [...], D2: [...], D3: [...] }
const pcsPorGrupo = computed<Record<string, any[]>>(() =>
  props.institutionalData?.pcsPorGrupo || {}
)

// ✅ grupos disponíveis ordenados
const gruposDisponiveis = computed(() =>
  Object.keys(pcsPorGrupo.value).sort()
)

// ✅ true se tem mais de um grupo
const temGrupos = computed(() => gruposDisponiveis.value.length > 1)

// ✅ todos os PCs em lista flat
const todosOsPcs = computed(() =>
  Object.values(pcsPorGrupo.value).flat()
)

const getStatusLabel = (statusId: number): string => {
  const id = Number(statusId)
  switch (id) {
    case CONST_INSTITUTIONAL_STATUS.DRAFT.id: return 'Rascunho'
    case CONST_INSTITUTIONAL_STATUS.ON_EDITING.id: return 'Em Edição'
    case CONST_INSTITUTIONAL_STATUS.ON_APPROVAL.id: return 'Em Aprovação'
    case CONST_INSTITUTIONAL_STATUS.ADJUSTMENT_REQUESTED.id: return 'Solicitado Ajuste'
    case CONST_INSTITUTIONAL_STATUS.APPROVED.id: return 'Aprovado'
    case CONST_INSTITUTIONAL_STATUS.PENDING_PUBLICATION.id: return 'Pendente Publicação'
    case CONST_INSTITUTIONAL_STATUS.PUBLISHED.id: return 'Publicado'
    case CONST_INSTITUTIONAL_STATUS.ACTIVE_INSTITUTIONAL.id: return 'Ativo'
    case CONST_INSTITUTIONAL_STATUS.ON_REISSUE.id: return 'Em Reedição'
    case CONST_INSTITUTIONAL_STATUS.ON_RE_APPROVAL.id: return 'Em Reaprovação'
    case CONST_INSTITUTIONAL_STATUS.ADJUSTMENT_REQUESTED_AGAIN.id: return 'Solicitado Reajuste'
    case CONST_INSTITUTIONAL_STATUS.REAPPROVED.id: return 'Reaprovado'
    case CONST_INSTITUTIONAL_STATUS.CANCELED.id: return 'Cancelado'
    case CONST_INSTITUTIONAL_STATUS.INSTITUTIONAL_COMPLETED.id: return 'Concluído'
    case CONST_INSTITUTIONAL_STATUS.HISTORICO.id: return 'Histórico'
    default: return ''
  }
}

// ✅ Gera e faz download do CSV
/*const gerarCsvDownload = (pcs: any[], nomeArquivo: string) => {
  if (!pcs?.length) return

  const header = ['Codigo PC', 'Nome', 'UF', 'Cidade', 'Segmento', 'Subrede']

  const rows = pcs.map((pc: any) => [
    pc.codpontocaptura,
    pc.nompontocaptura,
    pc.ufredeautoatndto || '-',
    pc.nomcidredeautoatndto || '-',
    pc.nomsegmmerclocalptoatndto || '-',
    pc.nomsubredeautoatndto || '-'
  ])

  const csvContent = [
    header.join(';'),
    ...rows.map((row: any) => row.join(';'))
  ].join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = nomeArquivo
  link.click()
  URL.revokeObjectURL(url)
} */

/*const gerarCsvDownload = (pcs: any[], nomeArquivo: string) => {
  if (!pcs?.length) return

  const header = ['Codigo PC', 'Nome', 'UF', 'Cidade', 'Segmento', 'Subrede']

  const rows = pcs.map((pc: any) => [
    pc.codpontocaptura ?? '-',
    pc.nompontocaptura ?? '-',        // 👈 usa direto
    pc.ufredeautoatndto ?? '-',
    pc.nomcidredeautoatndto ?? '-',
    pc.nomsegmmerclocalptoatndto ?? '-',
    pc.nomsubredeautoatndto ?? '-'
  ])

  const csvContent = [
    header.join(';'),
    ...rows.map(row => row.join(';'))
  ].join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url
  link.download = nomeArquivo
  link.click()

  URL.revokeObjectURL(url)
}*/


const onDownloadPcsCsvPorGrupo = (grupo: string) => {
  if (!props.institutionalData?.id) {
    messages.showWarningMsg('Nenhum PC disponível para download')
    return
  }

  institutionalStore.downloadPcsByTela(
    props.institutionalData.id,
    grupo
  )
}



const onDownloadPcsCsv = () => {
  const telaId = props.institutionalData?.id

  if (!telaId) {
    messages.showWarningMsg('Nenhum PC disponível para download')
    return
  }

  institutionalStore.downloadPcsByTela(telaId)
}

const onDownloadRedesCsv = async () => {
  let redes = props.institutionalData.redes || []

  // ✅ fallback: montar a partir das subredes selecionadas
  if (!redes.length) {
    redes = (redeSubredeStore.subredesSelecionadas || []).map((s: any) => ({
      codRede: s.codRedeAutoAtndto,
      codSubrede: s.codSubredeAutoAtndto
    }))
  }

  if (!redes.length) {
    console.warn('Nenhuma rede/subrede selecionada')
    return
  }

  if (!redeSubredeStore.subredes.length) {
    await redeSubredeStore.carregarSubredes()
  }

  const header = ['Rede', 'Nome Rede', 'Subrede', 'Nome Subrede']

  const rows = redes.map((r: any) => {
    const subrede = redeSubredeStore.subredes.find(
      s => s.codSubredeAutoAtndto === r.codSubrede
    )

    return [
      r.codRede,
      subrede?.nomRedeAutoAtndto || '-',
      r.codSubrede,
      subrede?.nomSubredeAutoAtndto || '-'
    ]
  })

  const csvContent = [
    header.join(';'),
    ...rows.map((row: any) => row.join(';'))
  ].join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', 'redes_subredes.csv')
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  URL.revokeObjectURL(url)
}
</script>

<style scoped>
.status-value {
  color: var(--globalAppSecondaryColor5) !important;
}
.institutional-identification {
  padding-bottom: 8px;
}
</style>
